import { useEffect, useState } from 'react';
import { useAdmin } from '@/hooks/useAdmin';
import { Link } from 'react-router-dom';
import { Copy, Loader2, Trash2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDuplicates() {
  const { authFetch } = useAdmin();
  const [pairs, setPairs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [threshold, setThreshold] = useState(0.85);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await authFetch(`${API}/admin/review/duplicates?threshold=${threshold}`);
      const d = await r.json();
      setPairs(d.pairs || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  async function del(id) {
    if (!window.confirm('Obrisati ovaj članak trajno?')) return;
    setBusyId(id);
    try {
      const r = await authFetch(`${API}/admin/articles/${id}`, { method: 'DELETE' });
      if (r.ok) { toast.success('Obrisano'); load(); }
    } finally { setBusyId(null); }
  }

  return (
    <div className="p-4 md:p-6" data-testid="admin-duplicates">
      <div className="flex items-center gap-3 mb-5">
        <Copy className="text-fuchsia-500" size={28} />
        <div>
          <h1 className="text-2xl font-bold">Detekcija duplikata</h1>
          <p className="text-sm text-zinc-400">Fuzzy podudaranje po naslovu + tijelu članka. Prag: <strong>{threshold}</strong></p>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-5">
        <label className="text-sm text-zinc-400">Prag podudarnosti:</label>
        <input type="range" min="0.5" max="1.0" step="0.05" value={threshold}
               onChange={e => setThreshold(+e.target.value)}
               data-testid="threshold-slider"
               className="w-48" />
        <span className="text-xs text-zinc-300 font-mono">{threshold.toFixed(2)}</span>
        <button onClick={load} data-testid="refresh-duplicates-btn"
                className="ml-auto px-3 py-1.5 rounded bg-fuchsia-600 hover:bg-fuchsia-700 text-white text-xs font-bold">
          Osvježi pretragu
        </button>
      </div>

      {loading ? <Loader2 className="animate-spin text-zinc-500" /> : (
        <div className="space-y-3">
          {pairs.length === 0 && (
            <div className="p-10 text-center text-zinc-500 border border-dashed border-zinc-800 rounded-xl">
              Nema otkrivenih duplikata za ovaj prag.
            </div>
          )}
          {pairs.map((p, idx) => (
            <div key={idx} data-testid={`dup-pair-${idx}`}
                 className="p-3 rounded-lg border border-zinc-800 bg-zinc-900/50">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs text-zinc-400">
                  Sličnost: <strong className="text-fuchsia-400">{Math.round(p.avg_similarity * 100)}%</strong>
                  {' '}(naslov {Math.round(p.title_similarity * 100)}%, tijelo {Math.round(p.body_similarity * 100)}%)
                </p>
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <ArticleRow a={p.article_a} onDelete={del} busyId={busyId} side="a"/>
                <ArticleRow a={p.article_b} onDelete={del} busyId={busyId} side="b"/>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ArticleRow({ a, onDelete, busyId, side }) {
  return (
    <div className="p-3 rounded border border-zinc-800 bg-zinc-950" data-testid={`dup-side-${side}-${a.id}`}>
      <p className="text-sm font-bold text-white truncate">{a.title_hr}</p>
      <p className="text-[11px] text-zinc-500 mt-0.5">
        {(a.publish_at || a.created_at || '').slice(0, 10)} · {a.status}
      </p>
      <div className="flex items-center gap-1.5 mt-2">
        <Link to={`/admin/articles/${a.id}/edit`} className="text-[11px] px-2 py-1 rounded bg-zinc-800 hover:bg-zinc-700">Uredi</Link>
        {a.slug && (
          <a href={`/clanak/${a.slug}`} target="_blank" rel="noopener noreferrer"
             className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded bg-zinc-800 hover:bg-zinc-700">
            <ExternalLink size={10}/> Otvori
          </a>
        )}
        <button onClick={() => onDelete(a.id)} disabled={busyId === a.id}
                data-testid={`del-side-${side}-${a.id}`}
                className="ml-auto text-[11px] px-2 py-1 rounded bg-red-800 hover:bg-red-700 text-white inline-flex items-center gap-1 disabled:opacity-50">
          <Trash2 size={10}/> Obriši
        </button>
      </div>
    </div>
  );
}
